import { Box, Flex } from '@lego/klik-ui';
import { Navigate, useParams } from 'react-router-dom';
import { BreadCrumb, Loader } from '@/components/common';
import { DeploymentComposeFile, DeploymentDetails } from '@/components/deployment';
import { DeploymentFileTable } from '@/components/deployment/DeploymentFileTable';
import { FleetRolloutCoverageCard } from '@/components/deployment/FleetRolloutCoverageCard';
import { GradualRolloutPlanner } from '@/components/deployment/gradual_rollout';
import { useDocumentTitle } from '@/hooks/common';
import { useDeployment } from '@/hooks/deployment';
import { useFleetRolloutWindowStats } from '@/hooks/rollout_windows';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { paths } from '@/utility/global_paths';

const DeploymentPageContent = ({
  workspaceId,
  fleetId,
  deploymentId,
}: {
  workspaceId: number;
  fleetId: number;
  deploymentId: number;
}) => {
  const { data: deployment, error, isLoading } = useDeployment(workspaceId, fleetId, deploymentId);
  useRegisterPageContext(deployment);
  useDocumentTitle(`Deployment #${deploymentId}`);
  const rolloutStats = useFleetRolloutWindowStats(workspaceId, fleetId);

  if (isLoading || error || !deployment) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  return (
    <Box>
      <Box>
        <Box>
          <BreadCrumb
            crumbs={[
              { label: 'Workspaces', path: paths.workspaces },
              {
                label: `Workspace #${workspaceId}`,
                path: paths.workspaceById(workspaceId),
              },
              {
                label: `Fleet #${deployment.fleetId}`,
                path: paths.fleetById(workspaceId, deployment.fleetId),
              },
              {
                label: `Deployment #${deployment.id}`,
                path: paths.deploymentById(workspaceId, deployment.fleetId, deployment.id),
              },
            ]}
          />
        </Box>
        <Flex direction={{ base: 'column', lg: 'row' }} gap={6} marginY={8}>
          <Flex direction="column" gap={6}>
            <DeploymentDetails deployment={deployment} workspaceId={workspaceId} />
            <DeploymentComposeFile deployment={deployment} workspaceId={workspaceId} />
          </Flex>
          <DeploymentFileTable files={deployment.files} workspaceId={workspaceId} />
        </Flex>
        <GradualRolloutPlanner
          deployment={deployment}
          fleetId={fleetId}
          rolloutStats={rolloutStats}
          workspaceId={workspaceId}
        />
        <Box marginTop={6}>
          <FleetRolloutCoverageCard rolloutStats={rolloutStats} />
        </Box>
      </Box>
    </Box>
  );
};

export const DeploymentPage = () => {
  const { workspaceId, fleetId, deploymentId } = useParams();

  if (
    !workspaceId ||
    !Number.parseInt(workspaceId, 10) ||
    !fleetId ||
    !Number.parseInt(fleetId, 10) ||
    !deploymentId ||
    !Number.parseInt(deploymentId, 10)
  ) {
    return <Navigate to={paths.notFound} />;
  }

  return (
    <DeploymentPageContent
      deploymentId={Number.parseInt(deploymentId, 10)}
      fleetId={Number.parseInt(fleetId, 10)}
      workspaceId={Number.parseInt(workspaceId, 10)}
    />
  );
};
